import React, { useState, useEffect } from 'react';
import {
    VStack,
    HStack,
    Button,
    Text,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    Box,
    IconButton,
    Switch,
    FormControl,
    FormLabel,
    Badge,
    Tooltip,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    StatArrow,
    Divider,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
} from '@chakra-ui/react';
import { FaMinus, FaPlus, FaPlay, FaRedo, FaHistory, FaCreditCard, FaCog, FaBolt, FaStop } from 'react-icons/fa';
import { useGame } from '../../context/GameContext';
import { useBalance } from '../../../context/BalanceContext';
import { motion } from 'framer-motion';
import LotteryTicketManager from './LotteryTicketManager';

const GameControls = () => {
    const { gameState, playGame, changeBetAmount, toggleAutoPlay, revealAllCards, flipCard } = useGame();
    const { balance } = useBalance();
    const { 
        betAmount, 
        isPlaying, 
        isAutoPlaying, 
        autoPlaySpeed: contextAutoPlaySpeed, 
        autoPlayCount: contextAutoPlayCount,
        autoPlayRemaining,
        sessionStats = {} 
    } = gameState;
    const [showHistory, setShowHistory] = useState(false);
    const [autoPlayCount, setAutoPlayCount] = useState(contextAutoPlayCount || 10);
    const [autoPlaySpeed, setAutoPlaySpeed] = useState(contextAutoPlaySpeed || 1000); // in milliseconds
    const [cardsReady, setCardsReady] = useState(false);

    // Sync local state with context
    useEffect(() => {
        if (contextAutoPlayCount && contextAutoPlayCount !== autoPlayCount) {
            setAutoPlayCount(contextAutoPlayCount);
        }
        if (contextAutoPlaySpeed && contextAutoPlaySpeed !== autoPlaySpeed) {
            setAutoPlaySpeed(contextAutoPlaySpeed);
        }
    }, [contextAutoPlayCount, contextAutoPlaySpeed]);

    const adjustBet = (amount) => {
        const newBet = Math.max(1, Math.min(500, betAmount + amount));
        changeBetAmount(newBet);
    };

    // Ensure sessionStats properties exist with default values
    const totalGames = sessionStats?.totalGames || 0;
    const totalWins = sessionStats?.totalWins || 0;
    const totalWagered = sessionStats?.totalWagered || 0;
    const netProfit = sessionStats?.netProfit || 0;
    const biggestWin = sessionStats?.biggestWin || 0;

    // Calculate win rate percentage with safe values
    const winRate = totalGames > 0 && totalWagered > 0
        ? ((totalWins / totalWagered) * 100).toFixed(1) 
        : '0.0';

    return (
        <VStack spacing={6} align="stretch">
            {/* Balance Display */}
            <Box
                as={motion.div}
                animate={balance < 100 ? { scale: [1, 1.05, 1] } : {}}
                transition={{ duration: 0.5, repeat: Infinity }}
                p={4}
                bg="linear-gradient(135deg, rgba(128, 90, 213, 0.2) 0%, rgba(0, 230, 118, 0.2) 100%)"
                border="2px solid"
                borderColor="purple.400"
                color="white"
                rounded="xl"
                boxShadow="0 4px 12px rgba(128, 90, 213, 0.3)"
                _hover={{
                    boxShadow: "0 6px 16px rgba(128, 90, 213, 0.4)",
                }}
            >
                <Stat>
                    <StatLabel color="purple.200">Your Balance</StatLabel>
                    <StatNumber fontSize="2xl" fontWeight="bold" color="white">
                        {balance?.jinu ? balance.jinu.toFixed(2) : '0.00'} JINU
                    </StatNumber>
                    <StatHelpText>
                        <StatArrow type={netProfit >= 0 ? "increase" : "decrease"} />
                        {netProfit.toFixed(2)} JINU
                    </StatHelpText>
                </Stat>
            </Box>

            {/* Bet Controls */}
            <Box>
                <HStack justify="space-between" mb={2}>
                    <Text color="purple.200">Scratch Card Price</Text>
                    <Badge colorScheme="purple" p={1} borderRadius="md">
                        {betAmount.toFixed(2)} JINU
                    </Badge>
                </HStack>
                <HStack spacing={4}>
                    <IconButton
                        icon={<FaMinus />}
                        onClick={() => adjustBet(-1)}
                        bg="transparent"
                        border="2px solid"
                        borderColor="purple.400"
                        color="purple.400"
                        rounded="full"
                        _hover={{
                            bg: 'purple.400',
                            color: 'black'
                        }}
                        isDisabled={isPlaying}
                    />
                    <Box flex={1}>
                        <Slider
                            value={betAmount}
                            onChange={(val) => changeBetAmount(val)}
                            min={1}
                            max={500}
                            step={1}
                            isDisabled={isPlaying}
                        >
                            <SliderTrack bg="whiteAlpha.200">
                                <SliderFilledTrack bg="purple.500" />
                            </SliderTrack>
                            <SliderThumb boxSize={6} />
                        </Slider>
                    </Box>
                    <IconButton
                        icon={<FaPlus />}
                        onClick={() => adjustBet(1)}
                        bg="transparent"
                        border="2px solid"
                        borderColor="purple.400"
                        color="purple.400"
                        rounded="full"
                        _hover={{
                            bg: 'purple.400',
                            color: 'black'
                        }}
                        isDisabled={isPlaying}
                    />
                </HStack>
            </Box>

            {/* Quick Bet Buttons */}
            <VStack spacing={2} w="100%">
                <HStack spacing={2} w="100%">
                    {[5, 10, 25, 50].map((amount) => (
                        <Button
                            key={amount}
                            flex={1}
                            size="sm"
                            onClick={() => changeBetAmount(amount)}
                            bg="transparent"
                            border="2px solid"
                            borderColor="purple.400"
                            color="purple.400"
                            rounded="full"
                            _hover={{
                                bg: 'purple.400',
                                color: 'black'
                            }}
                            isDisabled={isPlaying || balance < amount}
                            opacity={balance < amount ? 0.5 : 1}
                        >
                            {amount}
                        </Button>
                    ))}
                </HStack>
                <HStack spacing={2} w="100%">
                    {[100, 200, 500].map((amount) => (
                        <Button
                            key={amount}
                            flex={1}
                            size="sm"
                            onClick={() => changeBetAmount(amount)}
                            bg="transparent"
                            border="2px solid"
                            borderColor="purple.400"
                            color="purple.400"
                            rounded="full"
                            _hover={{
                                bg: 'purple.400',
                                color: 'black'
                            }}
                            isDisabled={isPlaying || balance < amount}
                            opacity={balance < amount ? 0.5 : 1}
                        >
                            {amount}
                        </Button>
                    ))}
                </HStack>
            </VStack>

            {/* Game Controls */}
            <VStack spacing={4}>
                <Button
                    w="100%"
                    size="lg"
                    leftIcon={isPlaying ? <FaRedo /> : <FaPlay />}
                    onClick={() => {
                        if (isPlaying) {
                            // If cards are already dealt, reveal them
                            revealAllCards();
                            setCardsReady(false);
                        } else {
                            // Otherwise get new cards
                            console.log(`Buying card for ${betAmount} JINU`);
                            playGame(false); // Explicitly pass false to indicate not a free play
                            setCardsReady(true);
                        }
                    }}
                    isDisabled={balance < betAmount && !isPlaying}
                    bgGradient={balance < betAmount && !isPlaying 
                        ? "linear(to-r, red.500, red.700)" 
                        : isPlaying 
                            ? "linear(to-r, green.500, green.700)"
                            : "linear(to-r, purple.500, purple.700)"
                    }
                    color="white"
                    rounded="full"
                    _hover={{
                        bgGradient: balance < betAmount && !isPlaying 
                            ? "linear(to-r, red.600, red.800)" 
                            : isPlaying 
                                ? "linear(to-r, green.600, green.800)"
                                : "linear(to-r, purple.600, purple.800)",
                    }}
                >
                    {balance < betAmount && !isPlaying 
                        ? "BUY JINU" 
                        : isPlaying 
                            ? "Reveal All" 
                            : "Get Cards"
                    }
                </Button>

                {cardsReady && isPlaying && (
                    <Text fontSize="sm" color="gray.300" textAlign="center">
                        Click on each card to reveal one by one, or use the button above to reveal all at once.
                    </Text>
                )}

                <HStack w="100%" justify="space-between">
                    <Tooltip label="View transaction history">
                        <Button
                            leftIcon={<FaHistory />}
                            onClick={() => setShowHistory(!showHistory)}
                            size="sm"
                            variant="outline"
                            colorScheme="purple"
                        >
                            History
                        </Button>
                    </Tooltip>
                    
                    <Tooltip label="Add funds to your wallet">
                        <Button
                            leftIcon={<FaCreditCard />}
                            size="sm"
                            variant="outline"
                            colorScheme="purple"
                        >
                            Add Funds
                        </Button>
                    </Tooltip>
                </HStack>

                <FormControl display="flex" alignItems="center" justifyContent="center" mb={2}>
                    <FormLabel htmlFor="autoplay" mb="0" color="purple.200">
                        Auto-Scratch
                    </FormLabel>
                    <Switch
                        id="autoplay"
                        isChecked={isAutoPlaying}
                        onChange={() => toggleAutoPlay({
                            speed: autoPlaySpeed,
                            count: autoPlayCount
                        })}
                        colorScheme="purple"
                    />
                    {isAutoPlaying && (
                        <Button
                            ml={2}
                            size="xs"
                            colorScheme="red"
                            leftIcon={<FaStop />}
                            onClick={() => toggleAutoPlay({ forceState: false })}
                        >
                            Stop
                        </Button>
                    )}
                </FormControl>

                {/* Auto-Play Settings */}
                <Box 
                    p={3} 
                    bg="whiteAlpha.100" 
                    borderRadius="md" 
                    mb={3}
                    display={isAutoPlaying ? "block" : "none"}
                >
                    <Text fontSize="sm" fontWeight="bold" mb={2} color="purple.200">
                        <FaCog style={{ display: 'inline', marginRight: '5px' }} /> Auto-Play Settings
                    </Text>
                    
                    {/* Display remaining auto-plays */}
                    {autoPlayRemaining > 0 && (
                        <HStack justify="space-between" mb={2}>
                            <Text fontSize="xs" color="gray.300">Remaining:</Text>
                            <Badge colorScheme="purple">{autoPlayRemaining}</Badge>
                        </HStack>
                    )}
                    
                    <FormControl mb={3}>
                        <FormLabel fontSize="xs" color="gray.300">Speed (ms)</FormLabel>
                        <Slider
                            min={200}
                            max={2000}
                            step={100}
                            value={autoPlaySpeed}
                            onChange={(val) => {
                                setAutoPlaySpeed(val);
                                toggleAutoPlay({ speed: val, forceState: true });
                            }}
                            colorScheme="purple"
                        >
                            <SliderTrack>
                                <SliderFilledTrack />
                            </SliderTrack>
                            <SliderThumb boxSize={4} />
                        </Slider>
                        <HStack justify="space-between" mt={1}>
                            <Badge colorScheme="green" fontSize="xs"><FaBolt /> Fast (200ms)</Badge>
                            <Text fontSize="xs" color="white">{autoPlaySpeed}ms</Text>
                            <Badge colorScheme="red" fontSize="xs">Slow (2000ms)</Badge>
                        </HStack>
                    </FormControl>
                    
                    <HStack spacing={2} justify="space-between" mb={2}>
                        <Text fontSize="xs" color="gray.300">Preset Counts:</Text>
                        <HStack>
                            {[10, 100, 500, 1000].map((count) => (
                                <Button
                                    key={count}
                                    size="xs"
                                    variant={autoPlayCount === count ? "solid" : "outline"}
                                    colorScheme="purple"
                                    onClick={() => {
                                        setAutoPlayCount(count);
                                        toggleAutoPlay({ count, forceState: true });
                                    }}
                                >
                                    {count}
                                </Button>
                            ))}
                        </HStack>
                    </HStack>
                    
                    <FormControl>
                        <FormLabel fontSize="xs" color="gray.300">Custom Count</FormLabel>
                        <NumberInput
                            min={1}
                            max={10000}
                            value={autoPlayCount}
                            onChange={(valueString) => {
                                const count = parseInt(valueString);
                                setAutoPlayCount(count);
                                toggleAutoPlay({ count, forceState: true });
                            }}
                            size="sm"
                        >
                            <NumberInputField />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                    </FormControl>
                </Box>
            </VStack>

            <Divider borderColor="whiteAlpha.300" />

            {/* Fast Track Section */}
            <Box 
                p={3} 
                bg="linear-gradient(135deg, rgba(128, 90, 213, 0.2) 0%, rgba(0, 230, 118, 0.2) 100%)"
                borderRadius="md" 
                mb={3}
                border="1px solid"
                borderColor="purple.400"
            >
                <Text fontSize="sm" fontWeight="bold" mb={2} color="purple.200">
                    <FaBolt style={{ display: 'inline', marginRight: '5px' }} /> Fast Track
                </Text>
                
                <Text fontSize="xs" color="gray.300" mb={2}>
                    Quick auto-play at maximum speed
                </Text>
                
                <HStack spacing={2} justify="space-between">
                    {[10, 20, 50, 100].map((count) => (
                        <Button
                            key={count}
                            flex={1}
                            size="sm"
                            colorScheme="purple"
                            variant="outline"
                            leftIcon={<FaBolt />}
                            onClick={() => {
                                setAutoPlaySpeed(200); // Set to fastest speed
                                setAutoPlayCount(count);
                                toggleAutoPlay({ 
                                    speed: 200, 
                                    count: count,
                                    forceState: true 
                                });
                            }}
                            isDisabled={isPlaying && !isAutoPlaying}
                            _hover={{
                                bg: 'purple.500',
                                color: 'white'
                            }}
                        >
                            {count}
                        </Button>
                    ))}
                </HStack>
            </Box>

            <Divider borderColor="whiteAlpha.300" />

            {/* Stats */}
            <Box
                p={4}
                bg="whiteAlpha.100"
                borderRadius="lg"
                border="1px solid"
                borderColor="whiteAlpha.200"
                boxShadow="inner"
            >
                <Text fontSize="sm" fontWeight="bold" mb={2} color="purple.200">
                    Session Stats
                </Text>
                <HStack justify="space-between">
                    <Text color="white">Cards Played:</Text>
                    <Text color="purple.200">{totalGames}</Text>
                </HStack>
                <HStack justify="space-between">
                    <Text color="white">Wins:</Text>
                    <Text color="green.400">{totalWins.toFixed(2)} JINU</Text>
                </HStack>
                <HStack justify="space-between">
                    <Text color="white">Total Spent:</Text>
                    <Text color="purple.400">{totalWagered.toFixed(2)} JINU</Text>
                </HStack>
                <HStack justify="space-between">
                    <Text color="white">Biggest Win:</Text>
                    <Text color="yellow.400">{biggestWin.toFixed(2)} JINU</Text>
                </HStack>
                <HStack justify="space-between" mt={2}>
                    <Text color="white">Return Rate:</Text>
                    <Badge colorScheme={winRate >= 75 ? "green" : "yellow"}>
                        {winRate}%
                    </Badge>
                </HStack>
            </Box>

            <LotteryTicketManager />
        </VStack>
    );
};

export default GameControls;
